<template>
  <div>
    <template slot="header">
      <div class="row">
        <div class="col-6">
          <h3 class="mb-0">{{ $t("ESTABLISHMENT_INTERNAL_ORDERS.ESTABLISHMENT_INTERNAL_ORDERS_LIST") }}</h3>
        </div>
        <div class="col-6 text-right">
          <base-button
            type="primary"
            icon
            size="sm"
            v-if="
              !hideAddButton &&
              $currentUserCan($permissions.PERM_CREATE_ESTABLISHMENT_INTERNAL_ORDERS)
            "
            @click="addOrder()"
          >
            <span class="btn-inner--icon">
              <i class="fas fa-plus"></i>
            </span>
            <span class="btn-inner--text">
              {{ $t("COMMON.ADD") }}
            </span>
          </base-button>
        </div>
      </div>
    </template>

    <div>
      <div
        class="col-12 d-flex flex-wrap filter-row"
      >
        <el-select
          class="select-primary pagination-select"
          v-model="pagination.perPage"
          :placeholder="$t('COMMON.PER_PAGE')"
        >
          <el-option
            class="select-primary"
            v-for="item in pagination.perPageOptions"
            :key="item"
            :label="item"
            :value="item"
          >
          </el-option>
        </el-select>

        <el-select
          class="select-primary pagination-select"
          v-model="selectedStatus"
          :placeholder="$t('COMMON.STATUS')"
        >
          <el-option
            class="select-primary"
            :key="0"
            :label="$t('COMMON.STATUS')"
            :value="null"
          >
          </el-option>
          <el-option
            v-for="status in statuses"
            :key="status"
            :label="$t(`ESTABLISHMENT_INTERNAL_ORDERS.STATUS_${status}`)"
            :value="status"
            class="select-primary"
          />
        </el-select>

        <el-select
          class="select-primary pagination-select"
          v-model="selectedInvoicingStatus"
          :placeholder="$t('ESTABLISHMENT_INTERNAL_ORDERS.INVOICING_STATUS')"
        >
          <el-option
            class="select-primary"
            :key="0"
            :label="$t('ESTABLISHMENT_INTERNAL_ORDERS.INVOICING_STATUS')"
            :value="null"
          >
          </el-option>
          <el-option
            v-for="status in invoicingStatuses"
            :key="status"
            :label="$t(`ESTABLISHMENT_INTERNAL_ORDERS.INVOICING_STATUS_${status}`)"
            :value="status"
            class="select-primary"
          />
        </el-select>

        <establishment-selector
          class="mr-2"
          :allOptionLabel="$t('COMMON.FILTER_ALL.PRODUCTION_ESTABLISHMENTS')"
          :establishment="selectedSourceEstablishment"
          :filterable="true"
          :showAll="true"
          v-if="$currentUserCan($permissions.PERM_VIEW_ANY_ESTABLISHMENTS)"
          @establishmentChanged="onSourceEstablishmentChanged"
        />

        <establishment-selector
          class="mr-2"
          :allOptionLabel="$t('COMMON.FILTER_ALL.SATELLITE_ESTABLISHMENTS')"
          :establishment="selectedDestinationEstablishment"
          :filterable="true"
          :showAll="true"
          v-if="$currentUserCan($permissions.PERM_VIEW_ANY_ESTABLISHMENTS)"
          @establishmentChanged="onDestinationEstablishmentChanged"
        />

        <div>
          <base-input
            v-model="query"
            type="search"
            prepend-icon="fas fa-search"
            :placeholder="$t('COMMON.SEARCH_HINT.NUMBER_OR_ESTABLISHMENT')"
            clearable
          />
        </div>
      </div>

      <el-table
        class="align-items-center table-flush"
        header-row-class-name="thead-light"
        :data="orders"
        @sort-change="sortChange"
        v-loading="loading"
      >
        <div slot="empty" v-if="loading">
          <img src="/img/loading.gif" style="height: 100px; width: 100px" />
        </div>

        <el-table-column
          :label="$t('COMMON.NUMBER')"
          prop="seq_number"
          sortable="custom"
          min-width="200px"
        >
          <template v-slot="{ row }">
            {{ row.seq_number ?? row.code }}
          </template>
        </el-table-column>
        <el-table-column :label="$t('ESTABLISHMENT_INTERNAL_ORDERS.PRODUCTION_ESTABLISHMENT')" min-width="200px">
          <template v-slot="{ row }">
            {{ row.sourceEstablishment?.company_name || row.source_establishment_id }}
          </template>
        </el-table-column>
        <el-table-column :label="$t('ESTABLISHMENT_INTERNAL_ORDERS.SATELLITE_ESTABLISHMENT')" min-width="200px">
          <template v-slot="{ row }">
            {{
              row.destinationEstablishment?.company_name ||
              row.destination_establishment_id
            }}
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('ESTABLISHMENT_INTERNAL_ORDERS.WEEK')"
          prop="week_starts_at"
          sortable="custom"
          min-width="200px"
        >
          <template v-slot="{ row }">
            <span v-if="row.week_starts_at">
              {{ $formatDate(row.week_starts_at, "ll") }} &rarr;
              {{ row.week_ends_at ? $formatDate(row.week_ends_at, "ll") : "-" }}
            </span>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('COMMON.STATUS')"
          prop="status"
          min-width="150px"
        >
          <template v-slot="{ row }">
            <establishment-internal-order-status-badge :order="row" />
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('ESTABLISHMENT_INTERNAL_ORDERS.INVOICING_STATUS')"
          prop="invoicing_status"
          min-width="170px"
        >
          <template v-slot="{ row }">
            <badge :type="invoicingBadgeType(row)">
              {{
                $t(
                  `ESTABLISHMENT_INTERNAL_ORDERS.INVOICING_STATUS_${row.invoicing_status}`
                )
              }}
            </badge>
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('COMMON.TOTAL')"
          prop="pricing.total"
          min-width="120px"
        >
          <template v-slot="{ row }">
            {{ $formatCurrency(row?.pricing?.total || 0) }}
          </template>
        </el-table-column>
        <el-table-column min-width="160px" align="center" fixed="right">
          <div slot-scope="{ row }" class="table-actions">
            <el-tooltip
              :content="$t('COMMON.VIEW')"
              placement="top"
              v-if="$currentUserCan($permissions.PERM_VIEW_ESTABLISHMENT_INTERNAL_ORDERS)"
            >
              <a
                type="text"
                @click="viewOrder(row)"
                class="table-action"
                data-toggle="tooltip"
                style="cursor: pointer"
              >
                <i class="fas fa-eye"></i>
              </a>
            </el-tooltip>

            <el-tooltip
              :content="$t('COMMON.EDIT')"
              placement="top"
              v-if="$currentUserCan($permissions.PERM_EDIT_ESTABLISHMENT_INTERNAL_ORDERS)"
            >
              <a
                type="text"
                @click="editOrder(row)"
                class="table-action"
                data-toggle="tooltip"
                style="cursor: pointer"
              >
                <i class="fas fa-edit"></i>
              </a>
            </el-tooltip>
            <el-tooltip
              :content="$t('COMMON.DELETE')"
              placement="top"
              v-if="
                row.status === 'DRAFT' &&
                $currentUserCan($permissions.PERM_DELETE_ESTABLISHMENT_INTERNAL_ORDERS)
              "
            >
              <a
                type="text"
                @click="deleteOrder(row)"
                class="table-action text-danger"
                data-toggle="tooltip"
                style="cursor: pointer"
              >
                <i class="fas fa-trash"></i>
              </a>
            </el-tooltip>
          </div>
        </el-table-column>
      </el-table>
    </div>

    <div
      slot="footer"
      class="mt-5 col-12 d-flex justify-content-center justify-content-sm-between flex-wrap"
    >
      <div class="">
        <p class="card-category">
          {{
            $t("COMMON.DISPLAY_FROM_X_TO_X_OF_X_ENTRIES", {
              from: total ? from + 1 : 0,
              to: to,
              of: total,
            })
          }}
        </p>
      </div>
      <base-pagination
        class="pagination-no-border"
        v-model="pagination.currentPage"
        :per-page="pagination.perPage"
        :total="total"
      />
    </div>
  </div>
</template>

<script>
import _ from "lodash";
import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  Option,
  Select,
  Table,
  TableColumn,
  Tooltip,
} from "element-ui";
import swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.css";
import requestErrorMixin from "@/mixins/request-error-mixin";
import { BasePagination } from "@/components";
import {
  establishmentInternalOrderStatuses,
  establishmentInternalOrderInvoicingStatuses,
  ESTABLISHMENT_INTERNAL_ORDER_INVOICING_STATUS_INVOICED,
  ESTABLISHMENT_INTERNAL_ORDER_INVOICING_STATUS_PARTIALLY_INVOICED,
} from "@/constants/establishmentInternalOrders";
import EstablishmentInternalOrderStatusBadge from "./EstablishmentInternalOrderStatusBadge.vue";
import EstablishmentSelector from "@/components/EstablishmentSelector.vue";

export default {
  name: "establishment-internal-order-list-table",

  components: {
    BasePagination,
    EstablishmentInternalOrderStatusBadge,
    EstablishmentSelector,
    [Tooltip.name]: Tooltip,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Dropdown.name]: Dropdown,
    [DropdownItem.name]: DropdownItem,
    [DropdownMenu.name]: DropdownMenu,
    [Select.name]: Select,
    [Option.name]: Option,
    [Button.name]: Button,
  },

  mixins: [requestErrorMixin],

  props: {
    filterEstablishment: {
      type: [String, Number],
      default: null,
    },
    filterSourceEstablishment: {
      type: [String, Number],
      default: null,
    },
    filterDestinationEstablishment: {
      type: [String, Number],
      default: null,
    },
    hideAddButton: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      query: null,
      sort: "-created_at",
      total: 0,
      pagination: {
        perPage: 20,
        currentPage: 1,
        perPageOptions: [20, 50, 100, 500],
      },
      orders: [],
      loading: true,
      selectedStatus: null,
      statuses: establishmentInternalOrderStatuses,
      selectedInvoicingStatus: null,
      invoicingStatuses: establishmentInternalOrderInvoicingStatuses,
      selectedSourceEstablishment: this.filterSourceEstablishment,
      selectedDestinationEstablishment: this.filterDestinationEstablishment,
      selectedEstablishment: this.filterEstablishment,
    };
  },

  computed: {
    from() {
      return this.pagination.perPage * (this.pagination.currentPage - 1);
    },

    to() {
      let highBound = this.from + this.pagination.perPage;
      if (this.total < highBound) {
        highBound = this.total;
      }
      return highBound;
    },
  },

  watch: {
    query: {
      handler: "getListDebounced",
      immediate: true,
    },
    pagination: {
      handler: "getList",
      immediate: false,
      deep: true,
    },
    selectedStatus: {
      handler: "getListDebounced",
      immediate: true,
    },
    selectedInvoicingStatus: {
      handler: "getListDebounced",
    },
    selectedSourceEstablishment: {
      handler: "getListDebounced",
    },
    selectedDestinationEstablishment: {
      handler: "getListDebounced",
    },
    filterEstablishment(value) {
      this.selectedEstablishment = value;
      this.getListDebounced();
    },
    filterSourceEstablishment(value) {
      this.selectedSourceEstablishment = value;
      this.getListDebounced();
    },
    filterDestinationEstablishment(value) {
      this.selectedDestinationEstablishment = value;
      this.getListDebounced();
    },
  },

  methods: {
    getListDebounced: _.debounce(function () {
      this.getList();
    }, 300),

    onSourceEstablishmentChanged(establishmentId) {
      this.selectedSourceEstablishment = establishmentId;
    },

    onDestinationEstablishmentChanged(establishmentId) {
      this.selectedDestinationEstablishment = establishmentId;
    },

    async getList() {
      this.loading = true;
      try {
        let params = {
          ...(this.sort ? { sort: this.sort } : {}),
          filter: {
            ...(this.query ? { search: encodeURIComponent(this.query) } : {}),
            ...(this.selectedStatus ? { status: this.selectedStatus } : {}),
            ...(this.selectedInvoicingStatus
              ? { invoicing_status: this.selectedInvoicingStatus }
              : {}),
            ...(this.selectedEstablishment
              ? { establishment: this.selectedEstablishment }
              : {}),
            ...(this.selectedSourceEstablishment
              ? { source_establishment: this.selectedSourceEstablishment }
              : {}),
            ...(this.selectedDestinationEstablishment
              ? { destination_establishment: this.selectedDestinationEstablishment }
              : {}),
          },
          page: {
            number: this.pagination.currentPage,
            size: this.pagination.perPage,
          },
          include: "sourceEstablishment,destinationEstablishment",
        };

        await this.$store.dispatch("establishmentInternalOrders/list", params);
        this.orders = this.$store.getters["establishmentInternalOrders/list"];
        this.total = this.$store.getters["establishmentInternalOrders/listTotal"];
      } catch (error) {
        console.error(error);
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
      } finally {
        this.loading = false;
      }
    },

    viewOrder(row) {
      this.$router.push({
        name: "View EstablishmentInternalOrder",
        params: { id: row.id },
      });
    },

    editOrder(row) {
      this.$router.push({
        name: "Edit EstablishmentInternalOrder",
        params: { id: row.id },
      });
    },

    addOrder() {
      this.$router.push({ name: "Add EstablishmentInternalOrder" });
    },

    async deleteOrder(row) {
      const confirmation = await swal.fire({
        title: this.$t("ESTABLISHMENT_INTERNAL_ORDERS.DELETE_ESTABLISHMENT_INTERNAL_ORDER"),
        type: "question",
        buttonsStyling: false,
        showCancelButton: true,
        confirmButtonText: this.$t("COMMON.YES"),
        cancelButtonText: this.$t("COMMON.NO"),
        confirmButtonClass: "btn btn-primary",
        cancelButtonClass: "btn btn-warning",
      });

      if (!confirmation.value) return;

      try {
        await this.$store.dispatch("establishmentInternalOrders/destroy", {
          id: row.id,
        });
        await this.getList();
        this.$notify({
          type: "success",
          message: this.$t("ESTABLISHMENT_INTERNAL_ORDERS.DELETE_ESTABLISHMENT_INTERNAL_ORDER"),
        });
      } catch (error) {
        await this.showRequestError(error);
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
      }
    },

    invoicingBadgeType(row) {
      switch (row.invoicing_status) {
        case ESTABLISHMENT_INTERNAL_ORDER_INVOICING_STATUS_INVOICED:
          return "success";
        case ESTABLISHMENT_INTERNAL_ORDER_INVOICING_STATUS_PARTIALLY_INVOICED:
          return "info";
        default:
          return "primary";
      }
    },

    sortChange({ prop, order }) {
      if (order === "descending") {
        this.sort = `-${prop}`;
      } else {
        this.sort = `${prop}`;
      }
      this.getList();
    },
  },
};
</script>
